// HOW A CUSTOM PART CATEGORY MEETS ITS PARENT'S TOTALS, in words.
//
// AssemblyGroups has shipped with its create button DISABLED since
// 2026-09-24, on purpose: `canCreate = !!(onCreate && totalsNote)`, and
// nothing ever passed either. HAL's objection was that "Beam Clamps (from
// Hangers)" tracked beside "Hangers" could double-count silently, and a
// promise to add a warning later is exactly how it would have shipped
// silently. So the component refused to create anything until somebody
// wrote the rule down.
//
// THE OWNER ANSWERED IT ON 2026-10-02 by choosing the assembly fork: the
// status stays on the HOST, a part never becomes statusable, and a custom
// category is counts and quantities only. That closes the double-count
// question rather than deferring it again - the two numbers cannot collide
// because they are not in the same sum:
//
//   Hangers        elements, with statuses, in the progress percentage.
//   Beam clamps    parts inside those hangers, counted and measured,
//                  carrying no status and - unless weighting is switched
//                  on for that one category - weighing nothing.
//
// This is the sentence the panel shows. It is kept in its own file because
// a rule that lives in a JSX attribute is a rule nobody can find, and
// because the test asserts the WORDS: if the product rule changes, the
// test should fail rather than the screen quietly saying the old thing.

export const PART_CATEGORY_TOTALS_RULE =
  "A custom category counts the PARTS inside the elements it is drawn from - it does not add elements. "
  + "The parts carry no status of their own; progress still follows the host elements, so tracking "
  + "\"Beam clamps\" beside \"Hangers\" cannot count the same work twice. "
  + "A category only reaches the project percentage if weighting is switched on for it, and it is off when created.";

// WEIGHTING OFF AT CREATION IS NOT WEIGHTING SAFE (leader's objection,
// 2026-10-02, and he is right).
//
// countsTowardProgress defaults false, which is what makes creating a
// category harmless today. But it CAN be turned on, and the moment it is,
// "Beam clamps" weighing on progress beside "Hangers" - which already
// weighs, and contains those clamps - counts the same work twice. That is
// precisely what the create button was disabled to prevent, so leaving the
// guard on the DEFAULT would move an unsettled rule from a disabled button
// to a switch nobody is watching.
//
// NOBODY HAS ASKED FOR WEIGHTING, so this is not built. It is written down
// here so that whoever turns it on finds the question instead of the
// default - and the test below asserts these words exist, so the sentence
// cannot quietly disappear in a tidy-up.
//
// WHAT THE ANSWER WILL HAVE TO BE, when it is asked: the transition has to
// resolve the overlap for the definitions that ALREADY exist - the server
// has utils/derivedCategories.overlapWith for exactly that - and refuse,
// or subtract, rather than silently add.
export const PART_CATEGORY_WEIGHTING_WARNING =
  "Weighting is off when a category is created, and that is what makes creating one safe. "
  + "Switching it on is a different question: a part category that weighs on progress alongside the "
  + "category it came from can count the same work twice, so the overlap has to be resolved before "
  + "any category is allowed to weigh.";

// A part may belong to more than one custom category. These are VIEWS,
// not a partition, and counting it in both is the right answer - but it
// has to be SAID, or somebody finds out by adding a second category and
// wondering which number is wrong.
export const PART_CATEGORY_OVERLAP_NOTE =
  "The same part can belong to more than one custom category - these are views of the model, not a split of it, "
  + "so it is counted in each. Two categories that overlap are not an error.";

// WHICH SOURCE A DEFINITION IS DRAWN FROM, as the dropdown produces it.
// A level the person stopped at is a WILDCARD, not a blank: stopping at
// "MEP Fabrication Hangers" means every family and type under it, which
// is the common case and must not be read as "a family whose name is the
// empty string".
export function definitionFrom({ source = {}, part = {}, measure = "count", name = "" }) {
  const pick = (o) => ({
    category: String((o && o.category) || ""),
    family: String((o && o.family) || ""),
    type: String((o && o.type) || ""),
  });
  return { name: String(name || "").trim(), source: pick(source), part: pick(part), measure };
}

// Is this definition answerable at all? A part with no source category is
// "every part in the model", which is not a category anybody means - and
// the server would accept it.
export function refuseDefinition(def) {
  if (!def || !def.source || !def.source.category) {
    return "Pick the category these parts come from first - without it this would count every part in the model.";
  }
  if (!def.name) return "Give the category a name.";
  return "";
}
